import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  featherBarChart2,
  featherBox,
  featherBriefcase,
  featherCalendar,
  featherClipboard,
  featherInfo,
  featherSettings,
  featherUser,
  featherUsers,
} from '@ng-icons/feather-icons';
import {
  heroCheckBadge,
  heroInformationCircle,
  heroPencil,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import {
  Company,
  CompanyStatisticsDto,
} from 'src/app/api/fn/generals/company.model';
import { CompanyService } from 'src/app/api/services/company.service';
import { ImageUploadModalComponent } from 'src/app/shared/component/image-upload-modal/image-upload-modal.component';
import { ToastService } from 'src/app/shared/service/toast.service';
import { APP_CONSTANTS } from '../../constant/constants';
import { VerificationModalComponent } from 'src/app/shared/component/verification-modal/verification-modal.component';
import { finalize } from 'rxjs';
import { CompanyStateService } from 'src/app/shared/service/company-state.service';

@Component({
  selector: 'app-user-company-layout',
  imports: [
    RouterModule,
    NgIcon,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ImageUploadModalComponent,
    VerificationModalComponent,
  ],
  templateUrl: './user-company-layout.component.html',
  viewProviders: [
    provideIcons({
      featherBriefcase,
      featherClipboard,
      featherUser,
      featherCalendar,
      featherInfo,
      featherUsers,
      featherBarChart2,
      featherSettings,
      heroCheckBadge,
      heroInformationCircle,
      heroXMark,
      heroPencil,
      featherBox,
    }),
  ],
})
export class UserCompanyLayoutComponent implements OnInit {
  @ViewChild('verificationModal')
  verificationModal!: VerificationModalComponent;
  company!: Company | null;
  stasts!: CompanyStatisticsDto;
  isLoading: boolean = true;
  isLoadingSend: boolean = false;
  isLoadingVerify: boolean = false;

  showVerificationModal: boolean = false;
  showModalUpdate: boolean = false;
  modalLoading: boolean = false;
  verificationStep = 1;
  verificationCode = '';
  countdown = 60;
  canResend: boolean = false;
  countdownInterval: any;

  constructor(
    private route: ActivatedRoute,
    private companyService: CompanyService,
    private companyState: CompanyStateService,
    private toastService: ToastService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.loadingData(id);
    } else {
      this.isLoading = false;
      this.toastService.showMessages('warning', ['ID de empresa inválido']);
      this.router.navigate(['/user/company']);
    }
  }

  loadingData(id: string) {
    this.companyService.getCompanyById(id).subscribe({
      next: (response) => {
        this.company = response.dto.company;
        if (this.company.verified) {
          this.companyState.setVerified(this.company.verified as '0' | '1');
        }
        this.companyService.getCompanyStatistics(id).subscribe({
          next: (statsResponse) => {
            this.stasts = statsResponse.dto;
            this.isLoading = false;
          },
          error: (statsError) => {
            this.isLoading = false;
            console.error(statsError);
            this.toastService.showMessages(
              'error',
              statsError.error?.listMessage || [
                'Ocurrió un problema al cargar las estadísticas de la empresa',
              ]
            );
          },
        });
      },
      error: (err) => {
        this.isLoading = false;
        console.error(err);
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || ['Ocurrió un problema al cargar la empresa']
        );
        this.router.navigate(['/user/company']);
      },
    });
  }

  isVerified(): boolean {
    if (this.company?.verified === '1') {
      return true;
    } else {
      return false;
    }
  }

  openModal() {
    this.showVerificationModal = true;
  }

  closeVerificationModal() {
    this.showVerificationModal = false;
  }

  handleResend() {
    if (!this.company) return;

    this.companyService
      .sendVerificationCode(this.company.email, this.company.idCompany)
      .subscribe({
        next: (res) => {
          this.toastService.showMessages('success', res.listMessage);
        },
        error: (err) => {
          this.toastService.showMessages(
            'error',
            err.error?.listMessage || ['Error al reenviar código']
          );
        },
      });
  }

  handleCompleted(data: { code: string }) {
    if (!this.company) return;

    this.verificationModal.isLoading = true;

    this.companyService
      .verifyCompany(this.company.idCompany, data.code)
      .pipe(
        finalize(() => {
          this.verificationModal.isLoading = false;
        })
      )
      .subscribe({
        next: (res) => {
          this.toastService.showMessages('success', res.listMessage);
          this.company!.verified = '1';
          this.companyState.setVerified('1');

          this.closeVerificationModal();
        },
        error: (err) => {
          this.toastService.showMessages(
            'error',
            err.error?.listMessage || ['Código inválido']
          );
        },
      });
  }

  uploadLogo(file: File) {
    if (!file || !this.company?.idCompany) return;

    this.modalLoading = true;

    this.companyService
      .updateUrlBannerMiniPage(this.company.idCompany, file)
      .subscribe({
        next: (res) => {
          this.toastService.showMessages('success', res.listMessage);
          this.modalLoading = false;
          this.showModalUpdate = false;
          if (this.company?.idCompany) {
            this.loadingData(this.company.idCompany);
          }
        },
        error: (err) => {
          this.toastService.showMessages(
            'error',
            err.error?.listMessage || ['Error al actualizar el logo.']
          );
          this.modalLoading = false;
          this.showModalUpdate = false;
        },
      });
  }

  openModalUploadImage() {
    this.showModalUpdate = true;
  }

  onCloseModalUploadImage() {
    this.showModalUpdate = false;
  }

  get companyImage(): string {
    const url = this.company?.urlBannerMiniPage;
    if (!url || url.trim() === '') {
      return APP_CONSTANTS.DEFAULT_IMAGE_COMPANY;
    }
    return url;
  }
}
